import React, { useEffect } from "react";

import { Experiment as Definition, useExperiment } from "./use-experiment";

type ExperimentProps<T> = {
    experiment: Definition<T>;
    render: (value: T) => React.ReactNode;
    indeterminate?: JSX.Element;
};

/**
 * ExperimentView is a component that performs a check on an experiment and its value. It will render the children if the experiment is enrolled and the value matches the expected value, otherwise it will render the fallback element.
 * @param experiment The experiment to check.
 * @param indeterminate The fallback element to render if the experiment is indeterminate.
 * @param render The children to render if the experiment is enrolled and the value matches the expected value.
 * @returns The ReactNode from the render method if the experiment is enrolled or not enrolled, otherwise the indeterminate element.
 */
export const ExperimentView = <T,>({
    experiment,
    indeterminate,
    render
}: ExperimentProps<T>) => {
    const experimentResult = useExperiment(experiment);
    const shouldManuallyFireExposure =
        !experiment.options.fireExperimentExposure;

    useEffect(() => {
        if (
            shouldManuallyFireExposure &&
            experimentResult.type === "enrolled"
        ) {
            experimentResult.fireExposure();
        }
    }, [experimentResult, experimentResult.type, shouldManuallyFireExposure]);

    if (experimentResult.type === "indeterminate" && indeterminate) {
        return indeterminate;
    }

    if (experimentResult.type === "enrolled") {
        return render(experimentResult.value);
    }

    // render based on default value if not enrolled
    return render(experiment.defaultValue);
};
